import moment from 'moment-timezone'

/**
 * List of deprecated IANA timezone names that should be filtered out
 * These are legacy aliases that have been replaced by modern timezone names
 */
const DEPRECATED_TIMEZONES = new Set([
  'Asia/Calcutta', // Use Asia/Kolkata instead
  'Asia/Katmandu', // Use Asia/Kathmandu instead
  'Asia/Dacca', // Use Asia/Dhaka instead
  'Asia/Thimbu', // Use Asia/Thimphu instead
  'Asia/Kashgar', // Use Asia/Urumqi instead
  'Asia/Rangoon', // Use Asia/Yangon instead
  'Asia/Saigon', // Use Asia/Ho_Chi_Minh instead
  'Asia/Chungking', // Use Asia/Chongqing instead
  'Asia/Ulan_Bator', // Use Asia/Ulaanbaatar instead
  'America/Argentina/ComodRivadavia', // Use America/Argentina/Catamarca instead
  'America/Atka', // Use America/Adak instead
  'America/Buenos_Aires', // Use America/Argentina/Buenos_Aires instead
  'America/Catamarca', // Use America/Argentina/Catamarca instead
  'America/Cordoba', // Use America/Argentina/Cordoba instead
  'America/Ensenada', // Use America/Tijuana instead
  'America/Fort_Wayne', // Use America/Indiana/Indianapolis instead
  'America/Indianapolis', // Use America/Indiana/Indianapolis instead
  'America/Jujuy', // Use America/Argentina/Jujuy instead
  'America/Knox_IN', // Use America/Indiana/Knox instead
  'America/Louisville', // Use America/Kentucky/Louisville instead
  'America/Mendoza', // Use America/Argentina/Mendoza instead
  'America/Montreal', // Use America/Toronto instead
  'America/Porto_Acre', // Use America/Rio_Branco instead
  'America/Rosario', // Use America/Argentina/Cordoba instead
  'America/Shiprock', // Use America/Denver instead
  'America/Virgin', // Use America/Port_of_Spain instead
  'Antarctica/South_Pole', // Use Pacific/Auckland instead
  'Asia/Chongqing', // Use Asia/Shanghai instead
  'Asia/Harbin', // Use Asia/Shanghai instead
  'Asia/Macao', // Use Asia/Macau instead
  'Asia/Tel_Aviv', // Use Asia/Jerusalem instead
  'Atlantic/Faeroe', // Use Atlantic/Faroe instead
  'Australia/ACT', // Use Australia/Sydney instead
  'Australia/Canberra', // Use Australia/Sydney instead
  'Australia/LHI', // Use Australia/Lord_Howe instead
  'Australia/NSW', // Use Australia/Sydney instead
  'Australia/North', // Use Australia/Darwin instead
  'Australia/Queensland', // Use Australia/Brisbane instead
  'Australia/South', // Use Australia/Adelaide instead
  'Australia/Tasmania', // Use Australia/Hobart instead
  'Australia/Victoria', // Use Australia/Melbourne instead
  'Australia/West', // Use Australia/Perth instead
  'Australia/Yancowinna', // Use Australia/Broken_Hill instead
  'Brazil/Acre', // Use America/Rio_Branco instead
  'Brazil/DeNoronha', // Use America/Noronha instead
  'Brazil/East', // Use America/Sao_Paulo instead
  'Brazil/West', // Use America/Manaus instead
  'Canada/Atlantic', // Use America/Halifax instead
  'Canada/Central', // Use America/Winnipeg instead
  'Canada/Eastern', // Use America/Toronto instead
  'Canada/Mountain', // Use America/Edmonton instead
  'Canada/Newfoundland', // Use America/St_Johns instead
  'Canada/Pacific', // Use America/Vancouver instead
  'Canada/Saskatchewan', // Use America/Regina instead
  'Canada/Yukon', // Use America/Whitehorse instead
  'Chile/Continental', // Use America/Santiago instead
  'Chile/EasterIsland', // Use Pacific/Easter instead
  'Cuba', // Use America/Havana instead
  'Egypt', // Use Africa/Cairo instead
  'Eire', // Use Europe/Dublin instead
  'Europe/Belfast', // Use Europe/London instead
  'Europe/Tiraspol', // Use Europe/Chisinau instead
  'GB', // Use Europe/London instead
  'GB-Eire', // Use Europe/London instead
  'GMT+0', // Use Etc/GMT instead
  'GMT-0', // Use Etc/GMT instead
  'GMT0', // Use Etc/GMT instead
  'Greenwich', // Use Etc/GMT instead
  'Hongkong', // Use Asia/Hong_Kong instead
  'Iceland', // Use Atlantic/Reykjavik instead
  'Iran', // Use Asia/Tehran instead
  'Israel', // Use Asia/Jerusalem instead
  'Jamaica', // Use America/Jamaica instead
  'Japan', // Use Asia/Tokyo instead
  'Kwajalein', // Use Pacific/Kwajalein instead
  'Libya', // Use Africa/Tripoli instead
  'Mexico/BajaNorte', // Use America/Tijuana instead
  'Mexico/BajaSur', // Use America/Mazatlan instead
  'Mexico/General', // Use America/Mexico_City instead
  'NZ', // Use Pacific/Auckland instead
  'NZ-CHAT', // Use Pacific/Chatham instead
  'Navajo', // Use America/Denver instead
  'PRC', // Use Asia/Shanghai instead
  'Pacific/Johnston', // Use Pacific/Honolulu instead
  'Pacific/Ponape', // Use Pacific/Pohnpei instead
  'Pacific/Samoa', // Use Pacific/Pago_Pago instead
  'Pacific/Truk', // Use Pacific/Chuuk instead
  'Pacific/Yap', // Use Pacific/Chuuk instead
  'Poland', // Use Europe/Warsaw instead
  'Portugal', // Use Europe/Lisbon instead
  'ROC', // Use Asia/Taipei instead
  'ROK', // Use Asia/Seoul instead
  'Singapore', // Use Asia/Singapore instead
  'Turkey', // Use Europe/Istanbul instead
  'US/Alaska', // Use America/Anchorage instead
  'US/Aleutian', // Use America/Adak instead
  'US/Arizona', // Use America/Phoenix instead
  'US/Central', // Use America/Chicago instead
  'US/East-Indiana', // Use America/Indiana/Indianapolis instead
  'US/Eastern', // Use America/New_York instead
  'US/Hawaii', // Use Pacific/Honolulu instead
  'US/Indiana-Starke', // Use America/Indiana/Knox instead
  'US/Michigan', // Use America/Detroit instead
  'US/Mountain', // Use America/Denver instead
  'US/Pacific', // Use America/Los_Angeles instead
  'US/Pacific-New', // Use America/Los_Angeles instead
  'US/Samoa', // Use Pacific/Pago_Pago instead
  'W-SU', // Use Europe/Moscow instead
])

/**
 * Get all available IANA timezones from moment-timezone
 * Filters out deprecated timezone aliases to avoid duplicates
 * @returns {Array} Array of timezone objects with value and label
 */
export const getAllTimezones = () => {
  // Get all timezone names from moment-timezone
  const timezoneNames = moment.tz.names()

  // Filter out deprecated timezones
  const validTimezoneNames = timezoneNames.filter(
    (tzName) => !DEPRECATED_TIMEZONES.has(tzName),
  )

  // Create timezone options with GMT offset
  return validTimezoneNames.map((tzName) => {
    // Get the current offset for this timezone
    const offset = moment.tz(tzName).utcOffset()
    const hours = Math.floor(Math.abs(offset) / 60)
    const minutes = Math.abs(offset) % 60
    const sign = offset >= 0 ? '+' : '-'
    const offsetString = `${sign}${String(hours).padStart(2, '0')}:${String(
      minutes,
    ).padStart(2, '0')}`

    return {
      value: tzName,
      label: `${tzName} (GMT${offsetString})`,
    }
  })
}

/**
 * Get timezone options sorted by offset, then alphabetically
 * @returns {Array} Sorted array of timezone objects
 */
export const getSortedTimezones = () => {
  const timezones = getAllTimezones()

  return timezones.sort((a, b) => {
    // Extract offset from label
    const offsetA = extractOffset(a.label)
    const offsetB = extractOffset(b.label)

    // Sort by offset first (most negative to most positive)
    if (offsetA !== offsetB) {
      return offsetA - offsetB
    }

    // Then sort alphabetically by timezone name
    return a.value.localeCompare(b.value)
  })
}

/**
 * Extract GMT offset in minutes from timezone label
 * @param {string} label - Timezone label like "America/New_York (GMT-5:00)"
 * @returns {number} Offset in minutes
 */
const extractOffset = (label) => {
  const match = label.match(/GMT([+-])(\d{1,2}):(\d{2})/)
  if (!match) return 0

  const sign = match[1] === '+' ? 1 : -1
  const hours = parseInt(match[2], 10)
  const minutes = parseInt(match[3], 10)

  return sign * (hours * 60 + minutes)
}

/**
 * Get browser timezone with fallback
 * @returns {string} IANA timezone string
 */
export const getBrowserTimezone = () => {
  try {
    if (
      typeof Intl !== 'undefined' &&
      Intl.DateTimeFormat().resolvedOptions().timeZone
    ) {
      return Intl.DateTimeFormat().resolvedOptions().timeZone
    }
  } catch (e) {
    // Ignore error
  }
  return ''
}

/**
 * Format timezone name for display
 * @param {string} timezone - IANA timezone string
 * @returns {string} Formatted timezone with GMT offset
 */
export const formatTimezoneLabel = (timezone) => {
  if (!timezone) return ''

  try {
    const offset = moment.tz(timezone).utcOffset()
    const hours = Math.floor(Math.abs(offset) / 60)
    const minutes = Math.abs(offset) % 60
    const sign = offset >= 0 ? '+' : '-'
    const offsetString = `${sign}${String(hours).padStart(2, '0')}:${String(
      minutes,
    ).padStart(2, '0')}`

    return `${timezone} (GMT${offsetString})`
  } catch (e) {
    return timezone
  }
}

/**
 * Get timezone offset in minutes for a given IANA timezone
 * @param {string} timezone - IANA timezone string
 * @returns {number|null} Offset in minutes or null on error
 */
export const getTimezoneOffsetMinutes = (timezone) => {
  if (!timezone) return null

  try {
    return moment.tz(timezone).utcOffset()
  } catch (e) {
    return null
  }
}
